import { Injectable, ApplicationRef, EnvironmentInjector, createComponent, ComponentRef } from '@angular/core';
import {ToastContainerComponent} from '../components/toast/toast-container/toast-container.component';

// toast.types.ts
export type ToastVariant = 'notice' | 'error' | 'success' | 'warning';

export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface ToastOptions {
  position?: ToastPosition;
  duration?: number; // milliseconds, 0 = no auto-dismiss
  closable?: boolean;
}

export interface ToastConfig {
  id: string;
  variant: ToastVariant;
  message: string;
  options: Required<ToastOptions>;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private containerRef?: ComponentRef<ToastContainerComponent>;
  private toastCounter = 0;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) {}

  success(message: string, options?: ToastOptions): void {
    this.show('success', message, options);
  }

  error(message: string, options?: ToastOptions): void {
    this.show('error', message, {...{duration: 0}, ...options});
  }

  warning(message: string, options?: ToastOptions): void {
    this.show('warning', message, options);
  }

  notice(message: string, options?: ToastOptions): void {
    this.show('notice', message, options);
  }

  private show(variant: ToastVariant, message: string, options?: ToastOptions): void {
    this.ensureContainer();

    const config: ToastConfig = {
      id: `toast-${++this.toastCounter}`,
      variant,
      message,
      options: {
        position: options?.position ?? 'top-right',
        duration: options?.duration ?? 4000,
        closable: options?.closable ?? true
      }
    };

    this.containerRef!.instance.addToast(config);
  }

  private ensureContainer(): void {
    if (!this.containerRef) {
      this.containerRef = createComponent(ToastContainerComponent, {
        environmentInjector: this.injector
      });

      this.appRef.attachView(this.containerRef.hostView);
      const domElem = this.containerRef.location.nativeElement;
      document.body.appendChild(domElem);
    }
  }
}
